import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Customers from './components/Customers';
import Users from './components/Users';
import Gateways from './components/Gateways';
import Systems from './components/Systems';
import Products from './components/Products';
import { CustomerSystems, Reports } from './components/index';
import { useAuth } from './contexts/AuthContext';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  const { setIsLoading } = useLoading();

  useEffect(() => {
    setIsLoading(true);
    window.scrollTo(0, 0);
    
    // Simulate brief loading for smooth transition
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [pathname, setIsLoading]);

  return null;
}

function LoadingOverlay() {
  const { isLoading } = useLoading();
  
  if (!isLoading) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(248, 250, 252, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        textAlign: 'center'
      }}>
        <div className="spinner-border" style={{ 
          width: '3rem', 
          height: '3rem',
          color: '#E63946',
          borderWidth: '0.3em'
        }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ 
          marginTop: '1rem', 
          color: '#1E3A8A',
          fontWeight: 500 
        }}>Loading...</p>
      </div>
    </div>
  );
}

function App() {
  const RoutesWithRole: React.FC = () => {
    const { user } = useAuth();
    return (
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/customers" element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <Customers />
          </ProtectedRoute>
        } />
        
        <Route path="/users" element={
          <ProtectedRoute allowedRoles={['superadmin', 'customer']}>
            <Users />
          </ProtectedRoute>
        } />
        
        <Route path="/gateways" element={
          <ProtectedRoute allowedRoles={['superadmin', 'customer', 'user']}>
            <Gateways />
          </ProtectedRoute>
        } />
        
        <Route path="/systems" element={
          <ProtectedRoute allowedRoles={['superadmin', 'customer', 'user']}>
            {(user?.role === 'customer' || user?.role === 'user') ? <CustomerSystems /> : <Systems />}
          </ProtectedRoute>
        } />

        {/* Customer Reports per system */}
        <Route path="/systems/:id/reports" element={
          <ProtectedRoute allowedRoles={['customer', 'superadmin', 'user']}>
            <Reports />
          </ProtectedRoute>
        } />
        
        <Route path="/products" element={
          <ProtectedRoute allowedRoles={['superadmin', 'customer', 'user']}>
            <Products />
          </ProtectedRoute>
        } />
        
        {/* Superadmin preview of a specific customer's systems using customer view */}
        <Route path="/customers/:id/preview" element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <CustomerSystems />
          </ProtectedRoute>
        } />
        
        {/* Profile and Settings Routes */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <div className="container">
              <h2>Profile</h2>
              <p>Profile management coming soon...</p>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/change-password" element={
          <ProtectedRoute>
            <div className="container">
              <h2>Change Password</h2>
              <p>Password change functionality coming soon...</p>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Unauthorized Route */}
        <Route path="/unauthorized" element={
          <div className="container text-center mt-5">
            <h2>Unauthorized Access</h2>
            <p>You don't have permission to access this page.</p>
          </div>
        } />
        
        {/* Default Redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Catch-all Route */}
        <Route path="*" element={
          <div className="container text-center mt-5">
            <h2>Page Not Found</h2>
            <p>The page you're looking for doesn't exist.</p>
          </div>
        } />
      </Routes>
    );
  };

  return (
    <AuthProvider>
      <LoadingProvider>
        <Router>
          <div className="App">
            <Navigation />
            <ScrollToTop />
            <LoadingOverlay />
            <main>
              <RoutesWithRole />
            </main>
          </div>
        </Router>
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;
